/**
 * Store switcher — portal-only topbar control. Shows the active store and lets a
 * multi-store user switch between their connected stores or connect a new one.
 * Switching re-fetches all data for the newly selected store.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Plus, Store } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { activeStoreFrom, useSelectStore, useStores } from '../hooks/usePortal';

export function StoreSwitcher() {
  const { data } = useStores();
  const select = useSelectStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const stores = data?.stores ?? [];
  const active = activeStoreFrom(stores);
  const label =
    active?.storeName || active?.shopDomain.replace('.myshopify.com', '') || 'Select store';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex max-w-[200px] items-center gap-2 rounded-xl border border-line py-1.5 pl-2.5 pr-2 text-sm transition-colors hover:bg-surface-subtle"
      >
        <Store className="h-4 w-4 shrink-0 text-ink-muted" />
        <span className="truncate font-medium text-ink">{label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-ink-subtle" />
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 mt-2 w-72 overflow-hidden rounded-2xl border border-line bg-surface shadow-pop"
          >
            <div className="border-b border-line px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              Your stores
            </div>
            <ul className="max-h-72 overflow-y-auto py-1">
              {stores.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      if (s.id !== active?.id) select.mutate(s.id);
                    }}
                    className="flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-surface-subtle"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-ink">
                        {s.storeName || s.shopDomain.replace('.myshopify.com', '')}
                      </span>
                      <span className="text-xs text-ink-subtle">{s.plan.toUpperCase()} plan</span>
                    </span>
                    {s.id === active?.id ? (
                      <Check className="h-4 w-4 shrink-0 text-brand-600" />
                    ) : null}
                  </button>
                </li>
              ))}
              {stores.length === 0 ? (
                <li className="px-4 py-3 text-sm text-ink-subtle">No stores connected yet.</li>
              ) : null}
            </ul>
            {data?.canConnectMore !== false ? (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/stores/connect');
                }}
                className="flex w-full items-center gap-2 border-t border-line px-4 py-3 text-left text-sm font-medium text-brand-600 hover:bg-surface-subtle"
              >
                <Plus className="h-4 w-4" /> Connect another store
              </button>
            ) : (
              <p className="border-t border-line px-4 py-3 text-xs text-ink-subtle">
                Store limit reached — upgrade your plan to connect more.
              </p>
            )}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
